// El detalle de un post al tocarlo en el dashboard: sus números, el link y, si salió del
// calendario, el camino de vuelta a él — ADR-2026-10-29-dashboard-de-metricas-de-instagram.

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, Film, Layers, Link2, Sparkles, X } from 'lucide-react';
import type { PostDeInstagram } from '../../../../lib/instagramMetricas';
import { useAperturaAnimada } from '../../../../hooks/useAperturaAnimada';
import { boton } from '../../botones';
import { MiniaturaDeInstagram } from './MiniaturaDeInstagram';
import { esReciente, fechaDelPost, numerosDelPost } from './metricas';

export function DetalleDePostDeInstagram({
  post,
  desdeAtlas,
  onAbrirEnCalendario,
  onCerrar,
}: {
  /** null = cerrado (el último sigue a la vista mientras sale). */
  post: PostDeInstagram | null;
  /** Salió del calendario de Atlas. */
  desdeAtlas: boolean;
  onAbrirEnCalendario: () => void;
  onCerrar: () => void;
}) {
  // 200 ms: en el teléfono es una hoja que vuelve entera hacia abajo.
  const { montado, visible } = useAperturaAnimada(post !== null, 200);
  const cerrarRef = useRef<HTMLButtonElement>(null);
  const dialogoRef = useRef<HTMLDivElement>(null);
  // El que se está cerrando se sigue dibujando hasta que termina de salir.
  const ultimo = useRef<PostDeInstagram | null>(null);
  if (post) ultimo.current = post;
  const p = post ?? ultimo.current;

  const abierto = post !== null;
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onCerrar();
      if (e.key !== 'Tab' || !dialogoRef.current) return;
      // El foco no sale del detalle: Tab da la vuelta adentro (lo de atrás queda tapado).
      const enfocables = Array.from(
        dialogoRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      );
      if (enfocables.length === 0) return;
      const primero = enfocables[0];
      const ultimoEnfocable = enfocables[enfocables.length - 1];
      const actual = document.activeElement;
      const adentro = dialogoRef.current.contains(actual);
      if (e.shiftKey && (actual === primero || !adentro)) {
        e.preventDefault();
        ultimoEnfocable.focus();
      } else if (!e.shiftKey && (actual === ultimoEnfocable || !adentro)) {
        e.preventDefault();
        primero.focus();
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [abierto, onCerrar]);

  // El foco entra al detalle y, al cerrarlo, vuelve a la casilla que lo abrió.
  // Se monta un frame después de abrir: por eso el foco va en otro efecto que espera a `montado`.
  useEffect(() => {
    if (!abierto) return;
    const antes = document.activeElement as HTMLElement | null;
    return () => antes?.focus?.();
  }, [abierto]);
  useEffect(() => {
    if (abierto && montado) cerrarRef.current?.focus();
  }, [abierto, montado]);

  if (!montado || !p) return null;
  const numeros = numerosDelPost(p);
  const tipo = p.tipo === 'reel' ? 'Reel' : p.formato === 'carrusel' ? 'Carrusel' : 'Post';

  return createPortal(
    <div
      className={`fixed inset-0 z-[150] flex items-end justify-center bg-black/40 p-0 transition-opacity ease-out sm:items-center sm:p-4 ${
        visible ? 'opacity-100 duration-300' : 'opacity-0 duration-200 sm:duration-150'
      }`}
      onClick={onCerrar}
    >
      <div
        ref={dialogoRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${tipo} de Instagram`}
        onClick={(e) => e.stopPropagation()}
        className={`flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white transition-[opacity,transform,filter] ease-out sm:rounded-3xl ${
          visible
            ? 'opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0'
            : abierto
              ? 'opacity-0 duration-300 motion-safe:translate-y-3 motion-safe:blur-[4px]'
              : // Saliendo: en el teléfono la hoja baja entera; en la compu sube y se desenfoca.
                'opacity-0 duration-200 motion-safe:translate-y-full sm:duration-150 sm:motion-safe:-translate-y-3 sm:motion-safe:blur-[4px]'
        }`}
      >
        <div className="flex items-center gap-2 border-b border-neutral-100 py-3 px-5">
          {p.tipo === 'reel' ? (
            <Film className="w-4 h-4 text-neutral-500" />
          ) : p.formato === 'carrusel' ? (
            <Layers className="w-4 h-4 text-neutral-500" />
          ) : null}
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-brand-black first-letter:uppercase">
            {tipo} · {fechaDelPost(p.fecha)}
          </p>
          <button
            ref={cerrarRef}
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          <div className="flex gap-4">
            <MiniaturaDeInstagram src={p.miniatura} className="h-28 w-28 shrink-0 rounded-xl" />
            <div className="min-w-0">
              {desdeAtlas && (
                <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] py-0.5 px-2 text-[11px] font-medium text-brand-primary">
                  <Sparkles className="w-3 h-3" /> Publicado desde Atlas
                </span>
              )}
              <p className="line-clamp-5 text-sm text-neutral-600">{p.texto || 'Sin texto.'}</p>
            </div>
          </div>

          {numeros.length > 0 ? (
            <dl className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {numeros.map((n) => (
                <div key={n.etiqueta} className="rounded-xl bg-neutral-50 py-2.5 px-3">
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">{n.etiqueta}</dt>
                  <dd className="mt-0.5 text-lg font-semibold text-brand-black">{n.valor}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-5 rounded-xl bg-neutral-50 py-3 px-4 text-sm text-neutral-500">
              Instagram todavía no tiene números de este post.
            </p>
          )}
          {esReciente(p.fecha) && (
            <p className="mt-3 text-xs text-neutral-500">
              Es reciente: Instagram sigue sumando números hasta 48 h después de publicar.
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-neutral-100 py-3 px-5">
          {desdeAtlas && (
            <button type="button" onClick={onAbrirEnCalendario} className={boton('secundario', { chico: true, extra: 'py-2' })}>
              <CalendarDays className="w-3.5 h-3.5" /> Abrir en el calendario
            </button>
          )}
          {p.link && (
            <a
              href={p.link}
              target="_blank"
              rel="noopener noreferrer"
              className={boton('primario', { chico: true, extra: 'py-2' })}
            >
              <Link2 className="w-3.5 h-3.5" /> Ver en Instagram
            </a>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
